// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import type { LeaderboardTemplate } from './types';

export interface LeaderboardTemplateCardProps {
  readonly template: LeaderboardTemplate;
  readonly selected: boolean;
  readonly onPress: (template: LeaderboardTemplate) => void;
}

export const LeaderboardTemplateCard = React.memo<LeaderboardTemplateCardProps>(
  ({ template, selected, onPress }) => {
    const scheme = useColorScheme();
    const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

    return (
      <Pressable
        onPress={() => onPress(template)}
        accessibilityRole="button"
        accessibilityLabel={template.title}
        accessibilityState={{ selected }}
        style={({ pressed }) => [
          styles.card,
          {
            backgroundColor: theme.colors.surface.secondary,
            borderColor: selected ? theme.colors.accent.indigo : theme.colors.border.secondary,
            opacity: pressed ? 0.8 : 1,
          },
          selected && { borderWidth: 2 },
        ]}
      >
        <Text style={[styles.title, { color: theme.colors.text.primary }]} numberOfLines={2}>
          {template.title}
        </Text>
        <Text
          style={[styles.description, { color: theme.colors.text.secondary }]}
          numberOfLines={3}
        >
          {template.description}
        </Text>
        <View style={styles.footer}>
          <Text style={[styles.columnCount, { color: theme.colors.text.tertiary }]}>
            {template.columns.length} columns
          </Text>
        </View>
      </Pressable>
    );
  },
);

LeaderboardTemplateCard.displayName = 'LeaderboardTemplateCard';

const styles = StyleSheet.create({
  card: {
    width: 220,
    minHeight: 140,
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginRight: 12,
    justifyContent: 'space-between',
  } satisfies ViewStyle,
  title: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 6,
  } satisfies TextStyle,
  description: {
    fontSize: 12,
    lineHeight: 17,
  } satisfies TextStyle,
  footer: {
    marginTop: 8,
  } satisfies ViewStyle,
  columnCount: {
    fontSize: 11,
    fontWeight: '500',
  } satisfies TextStyle,
});
